import { APIScope, APIScopePermission, AllAPIScopePermissions } from "../API/APIScope.Interfaces";
import { RoleEscalationEligibility } from "../RoutingGroup/RoutingGroup.Interface";
import { RoleInterface } from "./Roles.Interface";

export enum DefaultRoleTypes {
  ADMIN = "admin",
  AGENT = "agent",
  /**
   * Property-Manager persona. Slug is `"member"` for historical reasons:
   * 46 production orgs already reference it via `MemberPermission.roleSlug`,
   * inheriting from a Swivl-parent-org row that was promoted to a TS
   * default here (so the parent-org-role-inheritance crutch can go away).
   * The slug `"manager"` was vestigial — zero references in prod — and is
   * intentionally NOT a default; use this one instead.
   */
  MANAGER = "member"
}

export type DefaultRoleSlug = `${DefaultRoleTypes}`;

export const DEFAULT_ROLE_SLUGS: readonly DefaultRoleSlug[] = [DefaultRoleTypes.ADMIN, DefaultRoleTypes.AGENT, DefaultRoleTypes.MANAGER] as const;

export function isDefaultRoleSlug(slug: string | null | undefined): slug is DefaultRoleSlug {
  if (!slug) return false;
  return (DEFAULT_ROLE_SLUGS as readonly string[]).includes(slug);
}

export interface DefaultRoleInterface extends RoleInterface {
  actions: { [action in APIScope]: APIScopePermission };
  escalationEligibility: RoleEscalationEligibility;
}

export const DefaultRoles: { [type in DefaultRoleTypes]: DefaultRoleInterface } = {
  [DefaultRoleTypes.ADMIN]: {
    id: 1,
    orgId: 0,
    name: "Admin",
    description: "Admins can do anything",
    slug: "admin",
    escalationEligibility: "all",
    actions: {
      ...AllAPIScopePermissions,
      [APIScope.SWIVL_ADMIN_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.SWIVL_STAFF_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.LOCAL_ONLY]: APIScopePermission.FORBIDDEN
    }
  },
  [DefaultRoleTypes.AGENT]: {
    id: 2,
    orgId: 0,
    name: "Agent",
    description: "Agents can do most things",
    slug: "agent",
    escalationEligibility: "all",
    actions: {
      ...AllAPIScopePermissions,
      [APIScope.SWIVL_ADMIN_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.SWIVL_STAFF_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.LOCAL_ONLY]: APIScopePermission.FORBIDDEN
    }
  },
  [DefaultRoleTypes.MANAGER]: {
    id: 3,
    orgId: 0,
    name: "Manager",
    description: "Property managers handle escalations, edit member records, and view roles — but cannot administer the role system.",
    slug: "member",
    escalationEligibility: "all",
    actions: {
      ...AllAPIScopePermissions,
      [APIScope.SWIVL_ADMIN_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.SWIVL_STAFF_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.LOCAL_ONLY]: APIScopePermission.FORBIDDEN,
      [APIScope.ROLES_GET]: APIScopePermission.INHERITED,
      [APIScope.ROLES_SET]: APIScopePermission.FORBIDDEN
    }
  }
};
